import { fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import type { RootState } from '..';
import config from '../../config';
import { logout } from '../slices/authSlice';

const validateToken = (token: string | null): boolean => {
  if (!token) return false;
  try {
    // Basic JWT structure validation
    const parts = token.split('.');
    return parts.length === 3;
  } catch {
    return false;
  }
};

export const baseQuery = fetchBaseQuery({
  baseUrl: config.api.baseUrl,
  prepareHeaders: (headers, { getState }) => {
    // Don't override content-type if it's already set in the request
    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    headers.set('Accept', 'application/json');
    
    // Add token if available and valid
    const token = (getState() as RootState).auth.token;
    if (validateToken(token)) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    
    return headers;
  },
  credentials: 'include',
});

export const baseQueryWithReauth = async (args: any, api: any, extraOptions: any) => {
  try {
    // Get current token and state
    const state = api.getState() as RootState;
    const token = state.auth.token;
    const currentUser = state.auth.user;

    // Normalize args to object form
    const normalizedArgs = typeof args === 'string' ? { url: args } : { ...args };
    
    // Check if this is an auth endpoint (login, register, etc.)
    const isAuthEndpoint = normalizedArgs.url.includes('/auth/');
    
    // Check specifically for login, which should always skip token validation
    const isLoginEndpoint = normalizedArgs.url.endsWith('/auth/login');

    // Check if this is the users endpoint
    const isUsersEndpoint = normalizedArgs.url.includes('/users');
    
    // Skip token validation for login endpoint or endpoints with skipAuth flag
    if (!isLoginEndpoint && !normalizedArgs.skipAuth && !isAuthEndpoint && !validateToken(token)) {
      api.dispatch(logout());
      throw new Error('Invalid token');
    }

    // Make the request
    const result = await baseQuery(normalizedArgs, api, extraOptions);

    // Handle errors
    if (result.error) {
      const error = result.error as FetchBaseQueryError;
      if ('status' in error) {
        // Special handling for 403 errors on users endpoint for ShopEmployee
        if (error.status === 403 && isUsersEndpoint && currentUser?.role === 'ShopEmployee') {
          return { data: [] }; // Return empty array instead of error
        }
        
        if (error.status === 401 || (error.status === 403 && !isUsersEndpoint)) {
          // Don't logout if this is a login endpoint
          if (!isLoginEndpoint) {
            api.dispatch(logout());
            throw new Error('Authentication failed');
          }
        }
      }
    }

    return result;
  } catch (error) {
    return {
      error: { 
        status: 401, 
        data: { message: error instanceof Error ? error.message : 'Authentication failed' } 
      },
    };
  }
};

export default baseQueryWithReauth;